import { useEffect, useRef, useState } from 'react';

const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
const REDACT_CHAR = '█';
const STAGGER_MS = 18; // delay before each character starts unlocking
const SCRAMBLE_MS = 260; // how long a character flickers before it settles

function randomChar() {
  return SCRAMBLE_CHARS[Math.floor(Math.random() * SCRAMBLE_CHARS.length)];
}

// Spaces stay spaces in every phase. Line breaks can only happen at spaces
// (the monospace stack in .pd-decode-stable already makes every other glyph
// the same width), so keeping them where they are means the line wraps at
// exactly the same points whether it is blocks, flicker or the real words.
// Redacting the spaces too turned the phrase into one unbreakable run that
// wrapped differently from the text it was standing in for, and the card
// height jumped each time it changed — which, because the showcase scroll is
// a % transform of the track's height, made the whole marquee stutter.
const redactedFor = (text) => text.replace(/[^ ]/g, REDACT_CHAR);

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Renders `text` starting fully redacted (solid block per character), then
 * "unlocks" left to right with a brief matrix-style character flicker
 * before settling. Replays every time `active` transitions from false to
 * true (e.g. scrolling back into view in a loop) and snaps back to fully
 * redacted as soon as `active` goes false, so a looping marquee re-plays
 * the reveal on every pass rather than only once.
 */
export const DecodeReveal = ({ text, active, className = '' }) => {
  const [display, setDisplay] = useState(() => redactedFor(text));
  const [phase, setPhase] = useState('redacted'); // redacted -> decoding -> restored
  const frameRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => {
    if (!active) {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      startRef.current = null;
      setDisplay(redactedFor(text));
      setPhase('redacted');
      return undefined;
    }

    if (prefersReducedMotion()) {
      setDisplay(text);
      setPhase('restored');
      return undefined;
    }

    setPhase('decoding');
    startRef.current = null;
    let lastUpdate = -Infinity;
    const tick = (t) => {
      if (startRef.current == null) startRef.current = t;
      const elapsed = t - startRef.current;
      // Throttled to ~25fps: still reads as a smooth flicker, at under half
      // the DOM-mutation and layout work of updating every animation frame.
      if (t - lastUpdate < 40) {
        frameRef.current = requestAnimationFrame(tick);
        return;
      }
      lastUpdate = t;
      let out = '';
      let allSolved = true;
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ') {
          out += ' ';
          continue;
        }
        const startAt = i * STAGGER_MS;
        const solveAt = startAt + SCRAMBLE_MS;
        if (elapsed < startAt) {
          out += REDACT_CHAR;
          allSolved = false;
        } else if (elapsed < solveAt) {
          out += randomChar();
          allSolved = false;
        } else {
          out += text[i];
        }
      }
      setDisplay(out);
      if (allSolved) {
        setPhase('restored');
        return;
      }
      frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => frameRef.current && cancelAnimationFrame(frameRef.current);
  }, [active, text]);

  const markClass =
    phase === 'restored' ? 'pd-mark-restored' : phase === 'decoding' ? 'pd-mark-decoding' : 'pd-mark-redacted';

  return (
    <span
      className={`${markClass} pd-decode-stable ${className}`}
      aria-label={phase === 'restored' ? undefined : text}
    >
      {display}
    </span>
  );
};

export default DecodeReveal;
